import { format, unwrapFunctionStore } from 'svelte-i18n';

// import { generateAttachmentDefinitions, generateAttachmentsDataset } from '@client/components/charts/attachments';
import { generateEngagementDataset, generateEngagementDefinitions } from '@client/components/charts/engagement';

import { MessageLogParser, isBizcardMsglog } from '@client/utils/activity';

import { ACTIVITY_EVENTS, ACTIVITY_METRICS, ACTIVITY_STATS } from '@client/enums';

import { daysBetweenDates } from '@common/utils/dates';
import { getPercentageIncrease } from '@common/utils/numbers';

const MIN_DISTANCE = 0.999999;
const MAX_DISTANCE = 1.001001;

const $format = unwrapFunctionStore(format);

const BIZCARD_MSGLINK_PREFIX = 'bizcard:';

// Bizcard activities have no originating message — the API captures sender/domain from the
// request and skips _addMsgInfo entirely. buildMsgLogReports drops any row without a
// msglink, so give each card owner a synthetic one: their visits then group into a single
// pseudo-msglog, the way a real message aggregates its opens and clicks.
// The subject line is the only thing distinguishing one card's row from another's — the
// table has no From column, and every bizcard row's recipient is an anonymous visitor.
const bizcardSubject = (sender) =>
  sender
    ? $format('label.BIZCARD_SUBJECT_OWNED', { values: { owner: sender } })
    : $format('label.BIZCARD_SUBJECT');

const normalizeBizcardRow = (row) => ({
  ...row,
  msglink: row.msglink || `${BIZCARD_MSGLINK_PREFIX}${row.sender}`,
  msgsubject: row.msgsubject || bizcardSubject(row.sender),
  // Real bizcard rows omit `recipient` entirely — the visitor is anonymous. buildMsgLogReports
  // calls data.recipient.replace() unguarded, so an absent recipient throws once the row has a
  // msglink to get that far. Normalize to '' here rather than patching that call site, which
  // every other event type reaches with a real recipient.
  recipient: row.recipient || ''
});

// A bizcard visit carries its QR attribution in note.src, but every downstream seam keys
// off (objecttype, verb) alone. Emit a parallel `bizcardscan` row for QR visits so the
// legend, filter, map and KPI treat scans as an ordinary event type. A QR scan is a subset
// of a view, so it deliberately produces both rows and counts toward both.
const normalizeBizcardRows = (rows) =>
  rows.flatMap((row) => {
    if (row?.objecttype !== 'bizcardvisit') {
      return [row];
    }

    const visit = normalizeBizcardRow(row);

    return row.note?.src === 'qr' ? [visit, { ...visit, objecttype: 'bizcardscan' }] : [visit];
  });

let queryResult = [];
let queryStats = { ...ACTIVITY_STATS };
let queryResultPrevious = [];
let queryStatsPrevious = { ...queryStats };
let change = { ...queryStats };

let engagementChartData = {};
// let attachmentsChartData = {};

let markers = [];

export const formatResponse = (
  response,
  searchText,
  attributeFilters,
  tagFilters,
  startDate,
  endDate,
  leadscores,
  historyCache
) => {
  const startDateTimestamp = new Date(startDate).setHours(0, 0, 0, 0);
  const endDateTimestamp = new Date(endDate).setHours(23, 59, 59, 999);
  const current = [];
  const previous = [];

  normalizeBizcardRows(response)
    .sort((a, b) => new Date(a.utcts) - new Date(b.utcts))
    .forEach((item) => {
      if (historyCache[item.msglink]) {
        current.push(item);
      } else {
        if (new Date(item.utcts).getTime() < startDateTimestamp) {
          previous.push(item);
        } else if (
          new Date(item.utcts).getTime() >= startDateTimestamp &&
          new Date(item.utcts).getTime() <= endDateTimestamp
        ) {
          current.push(item);
        }
      }
    });

  queryResult = MessageLogParser.buildMsgLogReports(current, leadscores);

  // Pre-compute filter conditions to avoid repeated calculations
  const hasTagFilters = Object.keys(tagFilters).some((item) => tagFilters[item].enabled);
  const selectedTags = hasTagFilters
    ? new Set(Object.keys(tagFilters).filter((item) => tagFilters[item].enabled))
    : null;

  const hasAttributeFilters = Object.keys(attributeFilters).some((item) => attributeFilters[item].enabled);
  const searchTextLower = searchText?.toLowerCase();

  // Combined single-pass filtering
  queryResult = queryResult.filter((item) => {
    // Tag filtering
    if (hasTagFilters) {
      const tagItems = item.tags?.split(', ') ?? [];
      const hasMatchingTag = tagItems.some((tag) => selectedTags.has(tag));
      if (!hasMatchingTag) return false;
    }

    // Attribute filtering
    if (hasAttributeFilters) {
      let hasMatchingAttribute = false;

      for (const { objecttype, verb } of item.activity) {
        if (attributeFilters.adclicked?.enabled && objecttype === 'adclick') {
          hasMatchingAttribute = true;
          break;
        }
        if (attributeFilters.linkclick?.enabled && objecttype === 'linkclick') {
          hasMatchingAttribute = true;
          break;
        }
        if (
          attributeFilters.attachment?.enabled &&
          (objecttype === 'attachment' || objecttype === 'attachmentdownload' || objecttype === 'attachmentview')
        ) {
          hasMatchingAttribute = true;
          break;
        }
        if (attributeFilters.attachmentdownloaded?.enabled && objecttype === 'attachmentdownload') {
          hasMatchingAttribute = true;
          break;
        }
        if (attributeFilters.attachmentviewed?.enabled && objecttype === 'attachmentview') {
          hasMatchingAttribute = true;
          break;
        }
        if (
          attributeFilters.attachmentreplay?.enabled &&
          objecttype === 'attachment' &&
          (verb === 'myplayerlaunched' || verb === 'playerlaunched')
        ) {
          hasMatchingAttribute = true;
          break;
        }
        if (attributeFilters.website?.enabled && objecttype === 'websitevisit') {
          hasMatchingAttribute = true;
          break;
        }
        if (attributeFilters.bizcardview?.enabled && objecttype === 'bizcardvisit') {
          hasMatchingAttribute = true;
          break;
        }
        if (attributeFilters.bizcardscan?.enabled && objecttype === 'bizcardscan') {
          hasMatchingAttribute = true;
          break;
        }
        // Video filtering would go here if uncommented
      }

      if (!hasMatchingAttribute) return false;
    }

    // Search text filtering - restore original functionality with slight optimization
    if (searchTextLower) {
      const searchableText = Object.values(item).join(',').toLowerCase();
      if (!searchableText.includes(searchTextLower)) return false;
    }

    return true;
  });

  queryStats = MessageLogParser.calculateStats(queryResult).shift();

  let recipientCount = 0;
  let recipientViewCount = 0;
  let previousRecipientCount = 0;

  // Use Set for O(1) lookups instead of O(n) array.includes()
  const emailsSet = new Set();
  const engagementBreakdowns = {};
  const attachmentBreakdowns = {};

  // Pre-calculate expensive operations to avoid repeated allocations
  const randomFactors = new Map();
  const getRandomFactor = (key) => {
    if (!randomFactors.has(key)) {
      randomFactors.set(key, {
        lat: Math.random() * (MAX_DISTANCE - MIN_DISTANCE) + MIN_DISTANCE,
        lng: Math.random() * (MAX_DISTANCE - MIN_DISTANCE) + MIN_DISTANCE
      });
    }
    return randomFactors.get(key);
  };

  // Pre-compile regex for better performance
  const proxyRegex = /proxy|microsoft|relay/gi;

  // Pre-calculate current time for realtime checks
  const currentTime = new Date().getTime();
  const oneDayMs = 24 * 60 * 60 * 1000;

  // Pre-format event labels to avoid repeated format() calls
  const eventLabelsCache = new Map();
  const getEventLabel = (event) => {
    if (!eventLabelsCache.has(event)) {
      eventLabelsCache.set(event, $format(ACTIVITY_EVENTS[event]?.label) || '');
    }
    return eventLabelsCache.get(event);
  };

  // Single pass processing to avoid multiple iterations
  markers = [];
  for (const result of queryResult) {
    // Bizcard visitors are anonymous, so a bizcard pseudo-msglog has no real recips.
    // Letting its placeholder entries through would skew openrate and totalreach below.
    const bizcard = isBizcardMsglog(result);
    const resultRecipientsSet = new Set(bizcard ? [] : Object.values(result.recips));

    if (!bizcard) {
      recipientCount += Object.keys(result.recips).length;

      // Add emails to Set (O(1) operation)
      for (const email of Object.values(result.recips)) {
        emailsSet.add(email);
      }
    }

    // Process activities that have coordinates
    for (const activity of result.activity || []) {
      const { note, recipient, utcts, msgsubject, objecttype, haspitch, pitchinfo, verb } = activity;

      if (!note?.coords || !utcts) continue;

      const { coords = {}, isp = '' } = note;
      const { latitude, longitude } = coords;

      // Optimize expensive operations
      proxyRegex.lastIndex = 0; // Reset regex for reuse
      const proxy = proxyRegex.test(isp);
      const utctsTime = new Date(utcts).getTime();
      const realtime = currentTime - utctsTime < oneDayMs;
      const event = verb === 'clicked' || verb === 'visited' ? objecttype : verb;

      if (haspitch && pitchinfo) {
        const filenames = pitchinfo.map(({ filename }) => filename).filter(Boolean);
        // Attachment processing would go here if uncommented
      }

      if (objecttype === 'beaconview') {
        generateEngagementDefinitions(new Date(utcts), engagementBreakdowns);

        if (verb === 'viewed' && resultRecipientsSet.has(recipient)) {
          resultRecipientsSet.delete(recipient);
          recipientViewCount++;
        }
      }

      // Pre-calculate strings to avoid repeated operations
      const realtimeInfo = realtime ? (proxy ? 'realtime-proxy' : 'realtime') : '';
      const coordKey = `${latitude}_${longitude}`;
      const randomFactor = getRandomFactor(coordKey);
      const eventLabel = getEventLabel(event);
      const proxyText = proxy ? ' (Proxy)' : '';
      // Bizcard rows have no recipient — fall back to a label rather than rendering
      // an empty <b></b> and a leading space in the marker tooltip.
      const displayName = bizcard ? $format('label.BIZCARD_ANONYMOUS_VISITOR') : recipient;

      // Create marker object with pre-calculated values
      markers.push({
        title: `${displayName} ${msgsubject} ${realtimeInfo}`,
        content: `<b>${displayName}</b><br/>${msgsubject}<br/>${isp}${proxyText}<br/>${eventLabel}`,
        proxy,
        objecttype,
        verb,
        position: {
          lat: latitude * randomFactor.lat,
          lng: longitude * randomFactor.lng
        }
      });
    }
  }

  queryResultPrevious = MessageLogParser.buildMsgLogReports(previous, leadscores);
  queryStatsPrevious = MessageLogParser.calculateStats(queryResultPrevious).shift();

  // Use Set for O(1) lookups for previous emails too
  const previousEmailsSet = new Set();

  queryResultPrevious.forEach((result) => {
    // Same exclusion as the current-period loop above: a bizcard pseudo-msglog is not a
    // message and its visitor is not a recipient. Leaving it in here skewed only the
    // previous period, which surfaces as a wrong (or missing) change badge on tiles whose
    // current-period value looks correct.
    if (isBizcardMsglog(result)) {
      return;
    }

    Object.values(result.recips).forEach((email) => {
      previousEmailsSet.add(email);
    });

    previousRecipientCount += Object.keys(result.recips).length;
  });

  queryStats.openrate = ((recipientViewCount || 0) / recipientCount || 0) * 100;
  queryStats.totalreach = emailsSet.size;

  queryStatsPrevious.openrate = ((queryStatsPrevious.uniqueviews || 0) / previousRecipientCount || 0) * 100;
  queryStatsPrevious.totalreach = previousEmailsSet.size;

  change = Object.keys(queryStatsPrevious).reduce((acc, key) => {
    if (ACTIVITY_METRICS[key]) {
      const diff = getPercentageIncrease(queryStats[key], queryStatsPrevious[key]);
      acc[key] = diff.toFixed(0);
    }

    return acc;
  }, {});

  engagementChartData = {
    animation: {
      duration: 10000
    },
    datasets: []
  };

  // attachmentsChartData = {
  //   datasets: []
  // };

  engagementChartData = generateEngagementDataset(engagementBreakdowns, engagementChartData);
  // attachmentsChartData = generateAttachmentsDataset(attachmentBreakdowns, attachmentsChartData, startDate, endDate);

  queryStats = Object.assign(
    {},
    {
      openrate: queryStats.openrate,
      sent: queryStats.sent,
      uniqueviews: queryStats.uniqueviews,
      totalreach: queryStats.totalreach,
      uniqueclicks: queryStats.uniqueclicks,
      uniqueadclicks: queryStats.uniqueadclicks,
      uniquevisits: queryStats.uniquevisits,
      uniquevideolaunches: queryStats.uniquevideolaunches,
      uniquedocumentlaunches: queryStats.uniquedocumentlaunches,
      uniquedocumentdownloads: queryStats.uniquedocumentdownloads,
      uniquebizcardviews: queryStats.uniquebizcardviews,
      uniquebizcardscans: queryStats.uniquebizcardscans
    }
  );

  return {
    queryResult,
    queryStats,
    queryStatsPrevious,
    engagementChartData,
    // attachmentsChartData,
    change,
    markers
  };
};
